// src/utils/tabSyncManager.js - SIMPLIFIED VERSION (NO COORDINATION)
import { logger } from './logger';

export class TabSyncManager {
  constructor() {
    this.channel = new BroadcastChannel('workos-auth');
    this.listeners = new Map();
    
    // Listen for messages from other tabs
    this.channel.addEventListener('message', this.handleMessage.bind(this));
    
    // Handle tab closing
    window.addEventListener('beforeunload', this.handleTabClose.bind(this));
  }

  // Handle messages from other tabs
  handleMessage(event) {
    const { type, data, tabId, timestamp } = event.data;
    
    // Ignore messages from this tab
    if (tabId === this.getTabId()) return;
    
    logger.log(`Received tab sync message: ${type}`, data);
    
    switch (type) {
      case 'AUTH_STATE_CHANGED':
        this.notifyListeners('authStateChanged', data);
        break;
      
      case 'USER_LOGGED_OUT':
        this.notifyListeners('userLoggedOut', data);
        break;
      
      case 'SESSION_REFRESHED':
        this.notifyListeners('sessionRefreshed', data);
        break;
      
      case 'SESSION_EXPIRED':
        this.notifyListeners('sessionExpired', data);
        break;
        
      // REMOVED: REFRESH_TOKEN_REQUEST and REFRESH_TOKEN_RESPONSE cases
    }
  }

  // REMOVED: handleRefreshRequest, handleRefreshResponse, shouldHandleRefresh, requestCoordinatedRefresh

  // Broadcast authentication state change to other tabs
  broadcastAuthStateChange(authState) {
    this.sendMessage('AUTH_STATE_CHANGED', {
      user: authState.user,
      isAuthenticated: authState.isAuthenticated,
      sessionStatus: authState.sessionStatus,
      timestamp: Date.now()
    });
  }

  // Broadcast logout to other tabs
  broadcastLogout() {
    this.sendMessage('USER_LOGGED_OUT', {
      timestamp: Date.now(),
      reason: 'user_initiated'
    });
  }

  // Broadcast session refresh success (SIMPLIFIED - no coordination)
  broadcastSessionRefreshed(userData) {
    this.sendMessage('SESSION_REFRESHED', {
      user: userData,
      timestamp: Date.now()
    });
  }

  // Broadcast session expiry
  broadcastSessionExpired(reason) {
    this.sendMessage('SESSION_EXPIRED', {
      reason,
      timestamp: Date.now()
    });
  }

  // Send message to other tabs
  sendMessage(type, data) {
    try {
      this.channel.postMessage({
        type,
        data,
        tabId: this.getTabId(),
        timestamp: Date.now()
      });
    } catch (error) {
      logger.error('Error sending tab sync message:', error);
    }
  }

  // Get unique tab identifier
  getTabId() {
    if (!this._tabId) {
      this._tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    return this._tabId;
  }

  // Handle tab closing
  handleTabClose() {
    this.sendMessage('TAB_CLOSING', {
      tabId: this.getTabId()
    });
    this.channel.close();
  }

  // Add listener for tab sync events
  addListener(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
    
    // Return unsubscribe function
    return () => {
      const eventListeners = this.listeners.get(event);
      if (eventListeners) {
        eventListeners.delete(callback);
      }
    };
  }

  // Notify listeners of events
  notifyListeners(event, data) {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach(callback => {
        try {
          callback({ type: event, data });
        } catch (error) {
          logger.error(`Error in tab sync listener for ${event}:`, error);
        }
      });
    }
  }

  // Clean up resources
  destroy() {
    this.channel.close();
    this.listeners.clear();
    window.removeEventListener('beforeunload', this.handleTabClose.bind(this));
  }
}

// Create singleton instance
export const tabSyncManager = new TabSyncManager();